import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { MaterialIcon } from '@/components/MaterialIcon';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import {
  buildCustomDataRecordFromParse,
  mergeCustomDataRecords,
} from '@/utils/candidateResumeParse';
import type { CustomDataFormValues } from '@/utils/customDataForm';
import { candidateResumeParseResultAtom } from '../atom/candidatesAtom';
import useFetchCandidateProfilesCustomFields from '../api/useFetchCandidateProfilesCustomFields';
import useParseCandidateDocument from '../api/useParseCandidateDocument';

/** 対応ファイル形式（ResumeUploadSection と同じ定数を使用） */
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const ACCEPT_ATTR =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** ファイル形式バリデーション */
function isValidFile(file: File): boolean {
  if (ACCEPTED_MIME_TYPES.includes(file.type)) return true;
  const lower = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** 各ファイルスロットの状態 */
type SlotStatus = 'idle' | 'parsing' | 'done';

type FileSlotState = {
  status: SlotStatus;
  /** アップロード済みファイル名 */
  fileName: string | null;
  /** 解析済み blob の signed_id */
  blobSignedId: string | null;
  /** 解析結果から構築した RHF 形式の customData レコード */
  customData: CustomDataFormValues;
};

/** スロットの初期状態 */
const INITIAL_SLOT: FileSlotState = {
  status: 'idle',
  fileName: null,
  blobSignedId: null,
  customData: {},
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * ファイルから候補者情報を読み取り、新規登録フォームへ引き継ぐ大型フォームモーダル。
 *
 * - 氏名（必須・テキスト入力、AI解析で自動入力・編集可能）
 * - 履歴書（任意・ファイルスロット）
 * - 職歴書（任意・ファイルスロット）
 * の3入力を持つ。ただしこのモーダル自体は候補者を作成しない。
 * 「登録」ボタン押下時は解析結果（氏名・customData・blob signed_id）を
 * atom に保持したまま新規登録フォーム（/agent/candidates/new）へ遷移し、
 * 必須カスタムフィールドの補完・作成（POST）はフォーム側に委ねる。
 *
 * 各スロットのファイル選択時に useParseCandidateDocument を呼び出してAI解析を行い、
 * 解析結果のcustomDataを履歴書優先でマージして atom にセットする。
 */
export default function CandidatesResumeUploadModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const setParseResult = useSetAtom(candidateResumeParseResultAtom);

  // 氏名フィールド
  const [name, setName] = useState('');
  // 履歴書スロット
  const [resume, setResume] = useState<FileSlotState>(INITIAL_SLOT);
  // 職歴書スロット
  const [workHistory, setWorkHistory] = useState<FileSlotState>(INITIAL_SLOT);
  // 送信中フラグ
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 各スロット用のドラッグ状態
  const [isDraggingResume, setIsDraggingResume] = useState(false);
  const [isDraggingWorkHistory, setIsDraggingWorkHistory] = useState(false);

  // 各スロット用のファイル input ref（別々に管理して同一ファイル再選択を可能にする）
  const resumeInputRef = useRef<HTMLInputElement | null>(null);
  const workHistoryInputRef = useRef<HTMLInputElement | null>(null);

  const { data: customFields } = useFetchCandidateProfilesCustomFields();
  const { mutateAsync: parseDocument } = useParseCandidateDocument();

  /** いずれかのスロットが解析中かどうか */
  const isParsing = resume.status === 'parsing' || workHistory.status === 'parsing';
  /** 処理中（送信中 or 解析中）かどうか（UIを抑止する） */
  const isProcessing = isSubmitting || isParsing;
  /** 履歴書・職歴書のいずれかがアップロード済みかどうか */
  const hasAnyFile = resume.blobSignedId !== null || workHistory.blobSignedId !== null;
  /** 「作成」ボタンの disabled 条件（求職者名必須 + ファイル1つ以上必須） */
  const isSubmitDisabled = isProcessing || name.trim() === '' || !hasAnyFile;

  /**
   * 指定スロットのファイルを受け取り、解析して状態を更新する。
   */
  const handleFileForSlot = async (
    file: File,
    slotSetter: React.Dispatch<React.SetStateAction<FileSlotState>>
  ) => {
    if (!isValidFile(file)) {
      showErrorToast('PDF または Word(.docx) ファイルを選択してください');
      return;
    }

    // 解析中状態に移行
    slotSetter((prev) => ({ ...prev, status: 'parsing', fileName: file.name }));

    try {
      const parseResult = await parseDocument(file);

      // 解析成功: customData を構築してスロットに保存する
      const customData = buildCustomDataRecordFromParse(parseResult, customFields?.records);

      slotSetter({
        status: 'done',
        fileName: file.name,
        blobSignedId: parseResult.blob_signed_id,
        customData,
      });

      // 氏名フィールドが空のときだけ自動入力（ユーザー入力を上書きしない）
      if (!name.trim() && parseResult.name?.trim()) {
        setName(parseResult.name.trim());
      }
    } catch (error) {
      // 失敗: エラーメッセージを表示してスロットを idle に戻す
      showErrorToast(error instanceof Error ? error.message : '書類の読み込みに失敗しました');
      slotSetter(INITIAL_SLOT);
    }
  };

  /** 履歴書スロットのファイル選択ハンドラ */
  const handleResumeInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    void handleFileForSlot(file, setResume);
    // 同じファイルを再選択できるように value をリセット
    if (resumeInputRef.current) resumeInputRef.current.value = '';
  };

  /** 職歴書スロットのファイル選択ハンドラ */
  const handleWorkHistoryInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    void handleFileForSlot(file, setWorkHistory);
    // 同じファイルを再選択できるように value をリセット
    if (workHistoryInputRef.current) workHistoryInputRef.current.value = '';
  };

  /** 履歴書スロットのドロップハンドラ */
  const handleResumeDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingResume(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    void handleFileForSlot(file, setResume);
  };

  /** 職歴書スロットのドロップハンドラ */
  const handleWorkHistoryDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingWorkHistory(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    void handleFileForSlot(file, setWorkHistory);
  };

  /** 全内部状態をリセットする */
  const resetState = () => {
    setName('');
    setResume(INITIAL_SLOT);
    setWorkHistory(INITIAL_SLOT);
    setIsSubmitting(false);
    setIsDraggingResume(false);
    setIsDraggingWorkHistory(false);
  };

  /** モーダルを閉じる（処理中は無効化） */
  const handleClose = () => {
    if (isProcessing) return;
    resetState();
    onClose();
  };

  /** 解析結果を atom に保持し、新規登録フォームへ遷移する（候補者作成はフォーム側で行う） */
  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !hasAnyFile || isSubmitDisabled) return;

    setIsSubmitting(true);

    try {
      // 履歴書優先で customData をマージする（先勝ちルール）
      const mergedCustomData = mergeCustomDataRecords([resume.customData, workHistory.customData]);

      // 解析済み blob の signed_id を収集する（nullを除外）
      const blobSignedIds = [resume.blobSignedId, workHistory.blobSignedId].filter(
        (id): id is string => id !== null
      );

      // 解析結果を atom にセット（フォーム側でプリフィルに使用される）
      setParseResult({
        name: trimmedName,
        customData: mergedCustomData,
        blobSignedIds,
      });

      // 成功: トースト表示 → 新規登録フォームへ遷移 → モーダルを閉じる
      showSuccessToast('書類を読み込みました');
      await navigate({ to: '/agent/candidates/new' });
      resetState();
      onClose();
    } catch {
      showErrorToast('書類の読み込み結果の反映に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent className="flex max-h-[90vh] w-[972px] max-w-[95vw] flex-col overflow-hidden rounded-sm shadow-layer3!">
        {/* ヘッダー */}
        <div className="border-b border-solid border-border px-lg py-md">
          <DialogTitle className="m-0 text-heading-xs! text-main">ファイルから登録</DialogTitle>
        </div>

        {/* フォーム本体 */}
        <div className="flex flex-col gap-lg overflow-y-auto p-lg">
          {/* 氏名フィールド */}
          <div className="flex flex-col gap-xs">
            <div className="flex items-center gap-xs">
              <label htmlFor="candidate-name" className="m-0 text-heading-xxs text-main">
                求職者名
              </label>
              <span className="flex h-5 items-center justify-center rounded-full bg-danger_lighten px-sm text-tag-sm-highlight text-danger">
                必須
              </span>
            </div>
            <Input
              id="candidate-name"
              className="w-full"
              autoComplete="off"
              placeholder="例）山田 太郎"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          {/* 履歴書・職歴書スロット（横並び2カラム） */}
          <div className="grid grid-cols-1 items-start gap-lg md:grid-cols-2">
            {/* 履歴書スロット */}
            <FileUploadSlot
              label="履歴書"
              slot={resume}
              isDragging={isDraggingResume}
              inputRef={resumeInputRef}
              onInputChange={handleResumeInputChange}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingResume(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingResume(false);
              }}
              onDrop={handleResumeDrop}
              onClear={() => setResume(INITIAL_SLOT)}
              disabled={isProcessing}
            />

            {/* 職歴書スロット */}
            <FileUploadSlot
              label="職歴書"
              slot={workHistory}
              isDragging={isDraggingWorkHistory}
              inputRef={workHistoryInputRef}
              onInputChange={handleWorkHistoryInputChange}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingWorkHistory(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingWorkHistory(false);
              }}
              onDrop={handleWorkHistoryDrop}
              onClear={() => setWorkHistory(INITIAL_SLOT)}
              disabled={isProcessing}
            />
          </div>
        </div>

        {/* フッター */}
        <div className="flex justify-end gap-md border-t border-solid border-border px-lg py-md">
          <Button
            type="button"
            color="secondary"
            size="md"
            onClick={handleClose}
            disabled={isProcessing}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            color="primary"
            size="md"
            onClick={() => void handleSubmit()}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-xxs">
                <MaterialIcon
                  name="progress_activity"
                  filled
                  className="animate-spin text-[16px] leading-none"
                />
                登録中...
              </span>
            ) : (
              '登録'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** ファイルアップロードスロットの Props */
type FileUploadSlotProps = {
  /** スロットのラベル（「履歴書」「職歴書」） */
  label: string;
  /** スロットの現在状態 */
  slot: FileSlotState;
  /** ドラッグオーバー中かどうか */
  isDragging: boolean;
  /** ファイル input の ref */
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** ファイル input の onChange ハンドラ */
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** ドラッグオーバーハンドラ */
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  /** ドラッグリーブハンドラ */
  onDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  /** ドロップハンドラ */
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  /** スロットのクリア（削除）ハンドラ */
  onClear: () => void;
  /** 操作を無効化するかどうか */
  disabled: boolean;
};

/**
 * ファイルアップロードスロットコンポーネント。
 *
 * 状態に応じて以下を表示する:
 * - idle: ドラッグ&ドロップエリア＋「ファイルを選択」ボタン
 * - parsing: スピナー＋「読み込み中」
 * - done: ファイル名＋削除ボタン
 */
function FileUploadSlot({
  label,
  slot,
  isDragging,
  inputRef,
  onInputChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onClear,
  disabled,
}: FileUploadSlotProps) {
  return (
    <div className="flex flex-col gap-xs">
      <p className="m-0 text-heading-xxs text-main">{label}</p>

      {slot.status === 'parsing' ? (
        /* 解析中: スピナー＋メッセージ */
        <div className="flex h-[217px] flex-col items-center justify-center gap-sm rounded-sm border-2 border-dashed border-border bg-gray-50 p-lg">
          <MaterialIcon
            name="progress_activity"
            filled
            className="animate-spin text-[48px] leading-none text-primary"
          />
          <span className="text-body-md text-main">読み込み中</span>
        </div>
      ) : slot.status === 'done' && slot.fileName !== null ? (
        /* アップロード済み: ファイル名＋削除ボタン */
        <div
          className={[
            'flex h-20 items-center justify-between rounded-sm border border-solid border-border bg-white p-lg',
            disabled ? 'opacity-heigh' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="flex min-w-0 items-center gap-sm">
            <MaterialIcon
              name="description"
              filled
              className="shrink-0 text-[32px] leading-none text-main"
            />
            <span className="truncate text-body-sm text-main">{slot.fileName}</span>
          </div>
          <Button
            type="button"
            color="danger-secondary"
            size="sm"
            iconOnly
            onClick={onClear}
            disabled={disabled}
            aria-label={`${label}を削除`}
          >
            <MaterialIcon name="delete" filled className="text-[16px] leading-none" />
          </Button>
        </div>
      ) : (
        /* idle: ドラッグ&ドロップエリア */
        <>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={onInputChange}
            aria-label={`${label}ファイルを選択`}
            disabled={disabled}
          />
          <div
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={[
              'flex h-[217px] flex-col items-center justify-center gap-sm rounded-sm border-2 border-dashed p-lg transition-colors',
              isDragging ? 'border-primary bg-primary_lighten' : 'border-border bg-gray-50',
              disabled ? 'opacity-heigh' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <MaterialIcon name="cloud_upload" className="text-[48px] leading-none text-primary" />
            <div className="flex flex-col items-center gap-xxs text-main">
              <p className="m-0 text-body-md">ここにドラッグアンドドロップ</p>
              <p className="m-0 text-body-sm">または</p>
            </div>
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              <MaterialIcon name="folder" filled className="text-[20px] leading-none" />
              ファイルを選択
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
